import { useMemo, useState } from "react";
import {
  ArrowBack,
  AttachFile,
  ChatBubbleOutline,
  KeyboardArrowDown,
  MailOutline,
  MoreVert,
  PeopleOutline,
  Videocam,
} from "@mui/icons-material";
import { toast } from "sonner";
import UserAvatar from "../UserAvatar";
import { NewMessageModal } from "../../molecules/AppModal";
import {
  ApplicantTaskProgress,
  ApplicantTaskStatus,
  DocumentationStep,
  ResourceBookingSchema,
  ResourceSchema,
} from "../../data/types";
import { useAuthState } from "../../contexts/authcontext";
import { useMemberState } from "../../contexts/membercontext";
import { useUpdateBooking } from "../../hooks/memberhooks";
import { addBookingTaskActivity } from "../../api/documentationSteps";
import {
  findProgress,
  flattenStepTasks,
  formatConsultation,
  formatRelativeSubmitted,
  hasAgencyContact,
  isApprovedApplicant,
  isPendingApplicant,
  summarizeApplicantProgress,
  TASK_STATUS_OPTIONS,
  upsertProgressStatus,
} from "../../utils/documentationSteps";
import DocumentationStepsPanel from "./DocumentationStepsPanel";

type ApplicantTab = "approved" | "pending";

type DocumentationApplicantsPanelProps = {
  resource: ResourceSchema
  resourceId: string
  resourceType: string
  bookings: ResourceBookingSchema[]
};

type ViewState =
  | { kind: "list" }
  | { kind: "applicant"; booking: ResourceBookingSchema }
  | { kind: "activity"; booking: ResourceBookingSchema; stepId: string; taskId: string };

const splitName = (fullName?: string) => {
  const parts = (fullName || "").trim().split(/\s+/);
  return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
};

const StatusDot = ({ status }: { status: ApplicantTaskStatus }) => {
  const option = TASK_STATUS_OPTIONS.find((item) => item.id === status);
  return <span className={`mr-1 inline-block h-2 w-2 rounded-full ${option?.dot || "bg-[#9CA3AF]"}`} />;
};

const ApplicantField = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-lg border border-[#E9E9E9] px-3 py-2">
    <p className="font-raleway text-[12px] text-[#808080]">{label}</p>
    <p className="font-open-sans text-[14px] text-black">{value}</p>
  </div>
);

/** Applicants tab: pending/approved cards, View Applicant, and Task Activity. */
const DocumentationApplicantsPanel = ({
  resource,
  resourceId,
  resourceType,
  bookings,
}: DocumentationApplicantsPanelProps) => {
  const steps = resource.steps || [];
  const { authUser } = useAuthState();
  const { communityId } = useMemberState();
  const [tab, setTab] = useState<ApplicantTab>("approved");
  const [view, setView] = useState<ViewState>({ kind: "list" });
  const [statusFilter, setStatusFilter] = useState<ApplicantTaskStatus[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [editingSteps, setEditingSteps] = useState(false);
  const [comment, setComment] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [openTaskMenu, setOpenTaskMenu] = useState<string | null>(null);
  const [messageUsername, setMessageUsername] = useState("");
  const [isMessaging, setIsMessaging] = useState(false);

  const pending = useMemo(() => bookings.filter(isPendingApplicant), [bookings]);
  const approved = useMemo(() => bookings.filter(isApprovedApplicant), [bookings]);
  const currentList = tab === "pending" ? pending : approved;

  if (editingSteps) {
    return (
      <div className="mx-auto w-11/12 py-4">
        <button className="mb-3 flex items-center font-raleway text-primary-200" onClick={() => setEditingSteps(false)}>
          <ArrowBack className="mr-1" style={{ height: 18, width: 18 }} />
          Back to applicants
        </button>
        <DocumentationStepsPanel resource={resource} resourceId={resourceId} resourceType={resourceType} />
      </div>
    );
  }

  if (view.kind === "activity") {
    return (
      <TaskActivityView
        resource={resource}
        booking={view.booking}
        stepId={view.stepId}
        taskId={view.taskId}
        comment={comment}
        attachment={attachment}
        onCommentChange={setComment}
        onAttachmentChange={setAttachment}
        onBack={() => {
          setComment("");
          setAttachment(null);
          setView({ kind: "applicant", booking: view.booking });
        }}
        onSubmit={async () => {
          if (!communityId || !view.booking.id) return;
          const updated = await addBookingTaskActivity(communityId, view.booking.id, {
            stepId: view.stepId,
            taskId: view.taskId,
            message: comment,
            file: attachment || undefined,
            authorName: `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() || authUser?.email || "Admin",
            authorAvatar: authUser?.avatar,
            authorKind: "admin",
          });
          if (updated) {
            toast.success("Comment added.");
            setComment("");
            setAttachment(null);
            setView({ kind: "activity", booking: updated, stepId: view.stepId, taskId: view.taskId });
          } else {
            toast.error("Unable to save activity.");
          }
        }}
      />
    );
  }

  if (view.kind === "applicant") {
    return (
      <ApplicantDetailView
        resource={resource}
        booking={view.booking}
        steps={steps}
        statusFilter={statusFilter}
        filterOpen={filterOpen}
        openTaskMenu={openTaskMenu}
        onToggleFilter={() => setFilterOpen((prev) => !prev)}
        onToggleStatusFilter={(status) =>
          setStatusFilter((prev) => (prev.includes(status) ? prev.filter((item) => item !== status) : [...prev, status]))
        }
        onOpenMenu={setOpenTaskMenu}
        onBack={() => setView({ kind: "list" })}
        onEditSteps={() => setEditingSteps(true)}
        onOpenActivity={(stepId, taskId) => setView({ kind: "activity", booking: view.booking, stepId, taskId })}
        onMailAgency={(email) => {
          if (email) window.location.href = `mailto:${email}`;
        }}
        onStatusChange={async (stepId, taskId, status) => {
          const next = upsertProgressStatus(view.booking.applicant_checklist, stepId, taskId, status);
          setView({ kind: "applicant", booking: { ...view.booking, applicant_checklist: next } });
        }}
        onPersistStatus={async (checklist) => {
          if (!view.booking.id) return;
          // Persist via a nested updater so the hook reloads resource bookings.
        }}
        setView={setView}
      />
    );
  }

  return (
    <div className="mx-auto w-11/12 py-4">
      <div className="mb-4 flex gap-6 border-b border-[#E9E9E9]">
        {(["approved", "pending"] as ApplicantTab[]).map((option) => (
          <button
            key={option}
            className={`pb-2 font-raleway text-[15px] ${tab === option ? "border-b-2 border-primary-200 font-semibold text-primary-200" : "text-[#808080]"}`}
            onClick={() => setTab(option)}
          >
            {option === "approved" ? `Approved (${approved.length})` : `Pending (${pending.length})`}
          </button>
        ))}
      </div>

      {currentList.length === 0 ? (
        <div className="mx-auto my-16 flex w-[420px] flex-col rounded-xl bg-white p-8">
          <div className="mx-auto flex h-[80px] w-[80px] rounded-full bg-[#EFF8FA]">
            <PeopleOutline className="m-auto text-primary-400" style={{ height: 36, width: 36 }} />
          </div>
          <p className="mt-4 text-center font-raleway text-[20px] font-semibold text-primary-200">
            {tab === "approved" ? "No Approved Applications yet" : "No Pending Applications yet"}
          </p>
          <p className="mt-2 text-center font-open-sans text-[15px] text-[#333333]">
            {tab === "approved"
              ? "Approved applicants will appear here once you accept their requests."
              : "Applicant requests will appear here, you can approve or decline their requests."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {currentList.map((booking) =>
            tab === "pending" ? (
              <PendingApplicantCard
                key={booking.id}
                booking={booking}
                resourceId={resourceId}
                resourceType={resourceType}
              />
            ) : (
              <ApprovedApplicantCard
                key={booking.id}
                booking={booking}
                steps={steps}
                onView={() => setView({ kind: "applicant", booking })}
                onMessage={() => {
                  setMessageUsername(booking.username || booking.user_email || "");
                  setIsMessaging(true);
                }}
              />
            )
          )}
        </div>
      )}

      {isMessaging ? (
        <NewMessageModal
          isOpen={isMessaging}
          username={messageUsername}
          closeAppModal={() => setIsMessaging(false)}
        />
      ) : null}
    </div>
  );
};

type PendingCardProps = {
  booking: ResourceBookingSchema
  resourceId: string
  resourceType: string
};

const PendingApplicantCard = ({ booking, resourceId, resourceType }: PendingCardProps) => {
  const { updateBookingAsync, isLoadingBooking } = useUpdateBooking(booking.id || "", resourceId, resourceType as any);
  const name = splitName(booking.user_full_name);

  const handleStatus = async (status: "approved" | "canceled") => {
    const success = await updateBookingAsync({ ...booking, status });
    if (success) {
      toast.success(status === "approved" ? "Request approved." : "Request declined.");
    } else {
      toast.error("Unable to update this request.");
    }
  };

  return (
    <div className="flex flex-col rounded-xl border border-[#E9E9E9] bg-white p-5" data-testid={`pending-applicant-${booking.id}`}>
      <div className="mb-4 flex items-center gap-3">
        <UserAvatar firstName={name.firstName} lastName={name.lastName} avatar={booking.user_avatar} email={booking.user_email} username={booking.username} />
        <div>
          <p className="font-raleway text-[16px] font-semibold">{booking.user_full_name || booking.username || "Applicant"}</p>
          <p className="font-open-sans text-[13px] text-[#808080]">{booking.user_email || booking.username || "N/A"}</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <ApplicantField label="Submitted" value={formatRelativeSubmitted(booking.created)} />
        <ApplicantField label="Consultation Date & Time" value={formatConsultation(booking.start_time)} />
        <ApplicantField label="Business Category" value={booking.business_category || "N/A"} />
        <ApplicantField label="Type of Business" value={booking.business_type || "N/A"} />
        <ApplicantField label="Years of Operation" value={booking.years_of_operation || "N/A"} />
        <ApplicantField label="Notes" value={booking.notes || "N/A"} />
      </div>
      <button
        className="mt-4 w-full rounded-md bg-primary-200 py-2.5 font-raleway text-[15px] font-medium text-white disabled:opacity-60"
        disabled={isLoadingBooking}
        onClick={() => handleStatus("approved")}
      >
        Approve Request
      </button>
      <button
        className="mt-2 w-full rounded-md border border-[#E9E9E9] py-2.5 font-raleway text-[15px] font-medium text-primary-200 disabled:opacity-60"
        disabled={isLoadingBooking}
        onClick={() => handleStatus("canceled")}
      >
        Decline Request
      </button>
    </div>
  );
};

type ApprovedCardProps = {
  booking: ResourceBookingSchema
  steps: DocumentationStep[]
  onView: () => void
  onMessage: () => void
};

const ApprovedApplicantCard = ({ booking, steps, onView, onMessage }: ApprovedCardProps) => {
  const name = splitName(booking.user_full_name);
  const summary = summarizeApplicantProgress(steps, booking.applicant_checklist || []);
  const percent = summary.total === 0 ? 0 : Math.round((summary.completed / summary.total) * 100);
  const statusPills = TASK_STATUS_OPTIONS.filter((option) => summary.byStatus[option.id] > 0);

  return (
    <div className="flex flex-col rounded-xl border border-[#E9E9E9] bg-white p-5" data-testid={`approved-applicant-${booking.id}`}>
      <div className="mb-3 flex items-center gap-3">
        <UserAvatar firstName={name.firstName} lastName={name.lastName} avatar={booking.user_avatar} email={booking.user_email} username={booking.username} />
        <div>
          <p className="font-raleway text-[16px] font-semibold">{booking.user_full_name || booking.username || "Applicant"}</p>
          <p className="font-open-sans text-[13px] text-[#808080]">{booking.user_email || booking.username || "N/A"}</p>
        </div>
      </div>
      <p className="mb-1 font-raleway text-[13px] text-[#4D4D4D]">
        {summary.total === 0 ? "No tasks added yet" : `${summary.completed} of ${summary.total} tasks done`}
      </p>
      <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-[#E9E9E9]">
        <div className="h-full bg-[#22C55E]" style={{ width: `${percent}%` }} />
      </div>
      <div className="mb-4 flex flex-wrap gap-2">
        {summary.total === 0 ? (
          <span className="rounded-full border border-[#E9E9E9] px-3 py-1 font-raleway text-[12px] text-[#808080]">No Info yet</span>
        ) : (
          statusPills.map((option) => (
            <span key={option.id} className="inline-flex items-center rounded-full border border-[#E9E9E9] px-3 py-1 font-raleway text-[12px] text-[#4D4D4D]">
              <StatusDot status={option.id} />
              {summary.byStatus[option.id]} in {option.label}
            </span>
          ))
        )}
        {summary.newComments > 0 ? (
          <span className="inline-flex items-center rounded-full bg-[#EFF8FA] px-3 py-1 font-raleway text-[12px] text-primary-200">
            <ChatBubbleOutline className="mr-1" style={{ height: 14, width: 14 }} />
            {summary.newComments} New Comments
          </span>
        ) : null}
      </div>
      <button className="w-full rounded-md bg-primary-200 py-2.5 font-raleway text-[15px] font-medium text-white" onClick={onView}>
        View Applicant
      </button>
      <div className="mt-3 flex gap-2">
        <button className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6] text-[#808080]" aria-label="Video" disabled title="No meeting link">
          <Videocam style={{ height: 18, width: 18 }} />
        </button>
        <button
          className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6] text-[#808080]"
          aria-label="Email"
          onClick={() => {
            if (booking.user_email) window.location.href = `mailto:${booking.user_email}`;
          }}
        >
          <MailOutline style={{ height: 18, width: 18 }} />
        </button>
        <button className="flex h-10 w-10 items-center justify-center rounded-md bg-[#F3F4F6] text-[#808080]" aria-label="Message" onClick={onMessage}>
          <ChatBubbleOutline style={{ height: 18, width: 18 }} />
        </button>
      </div>
    </div>
  );
};

type ApplicantDetailViewProps = {
  resource: ResourceSchema
  booking: ResourceBookingSchema
  steps: DocumentationStep[]
  statusFilter: ApplicantTaskStatus[]
  filterOpen: boolean
  openTaskMenu: string | null
  onToggleFilter: () => void
  onToggleStatusFilter: (status: ApplicantTaskStatus) => void
  onOpenMenu: (id: string | null) => void
  onBack: () => void
  onEditSteps: () => void
  onOpenActivity: (stepId: string, taskId: string) => void
  onMailAgency: (email?: string) => void
  onStatusChange: (stepId: string, taskId: string, status: ApplicantTaskStatus) => void
  onPersistStatus: (checklist: ApplicantTaskProgress[]) => void
  setView: (view: ViewState) => void
};

const ApplicantDetailView = ({
  resource,
  booking,
  steps,
  statusFilter,
  filterOpen,
  openTaskMenu,
  onToggleFilter,
  onToggleStatusFilter,
  onOpenMenu,
  onBack,
  onEditSteps,
  onOpenActivity,
  onMailAgency,
  onStatusChange,
  setView,
}: ApplicantDetailViewProps) => {
  const { updateBookingAsync } = useUpdateBooking(booking.id || "", resource.id, "documentation");
  const name = splitName(booking.user_full_name);
  const summary = summarizeApplicantProgress(steps, booking.applicant_checklist || []);
  const percent = summary.total === 0 ? 0 : Math.round((summary.completed / summary.total) * 100);
  const tasks = flattenStepTasks(steps).filter((task) => {
    if (statusFilter.length === 0) return true;
    const status = findProgress(booking.applicant_checklist, task.step_id, task.id)?.status || "not_started";
    return statusFilter.includes(status);
  });

  const persistStatus = async (stepId: string, taskId: string, status: ApplicantTaskStatus) => {
    const next = upsertProgressStatus(booking.applicant_checklist, stepId, taskId, status);
    onStatusChange(stepId, taskId, status);
    const success = await updateBookingAsync({ ...booking, applicant_checklist: next });
    if (success) {
      setView({ kind: "applicant", booking: { ...booking, applicant_checklist: next } });
    } else {
      toast.error("Unable to update task status.");
    }
  };

  return (
    <div className="mx-auto w-11/12 py-4">
      <div className="mb-4 flex items-center">
        <button className="mr-3 flex h-10 w-10 items-center justify-center rounded-full border border-[#E9E9E9]" onClick={onBack} aria-label="Back">
          <ArrowBack />
        </button>
        <div className="flex items-center gap-3">
          <UserAvatar firstName={name.firstName} lastName={name.lastName} avatar={booking.user_avatar} email={booking.user_email} username={booking.username} size="lg" />
          <div>
            <p className="font-raleway text-[18px] font-semibold">{booking.user_full_name || booking.username}</p>
            <p className="font-open-sans text-[13px] text-[#808080]">{booking.user_email || booking.username}</p>
          </div>
        </div>
        <button
          className="ml-auto rounded-md bg-primary-200 px-4 py-2 font-raleway text-[14px] font-medium text-white"
          onClick={onEditSteps}
          data-testid="applicant-edit-steps"
        >
          {steps.length === 0 ? "Add Steps" : "Edit Steps"}
        </button>
      </div>

      {steps.length === 0 ? (
        <div className="mx-auto my-10 flex w-[460px] flex-col rounded-xl bg-white p-8">
          <p className="text-center font-raleway text-[20px] font-semibold text-black">No Checklist Created</p>
          <p className="my-3 text-center font-open-sans text-[15px] text-[#4D4D4D]">
            Select a checklist to guide this applicant through the steps needed to start and run their business.
          </p>
          <button className="rounded-md bg-primary-200 py-2.5 font-raleway text-white" onClick={onEditSteps}>
            Add Steps
          </button>
        </div>
      ) : (
        <>
          <p className="mb-2 font-open-sans text-[14px] text-[#4D4D4D]">View and track tasks, documents, and progress for this applicant.</p>
          <div className="mb-4 flex items-center">
            <div className="flex-1">
              <p className="mb-1 font-raleway text-[13px]">{summary.completed} of {summary.total} tasks done</p>
              <div className="h-1.5 w-full max-w-md overflow-hidden rounded-full bg-[#E9E9E9]">
                <div className="h-full bg-[#22C55E]" style={{ width: `${percent}%` }} />
              </div>
            </div>
            <div className="relative">
              <button className="flex items-center rounded-md border border-[#E9E9E9] bg-white px-3 py-2 font-raleway text-[13px]" onClick={onToggleFilter}>
                Filter By <KeyboardArrowDown className="ml-1" />
              </button>
              {filterOpen ? (
                <div className="absolute right-0 z-10 mt-1 w-52 rounded-lg border border-[#E9E9E9] bg-white p-2 shadow-md">
                  {TASK_STATUS_OPTIONS.map((option) => (
                    <label key={option.id} className="flex items-center gap-2 px-2 py-1 font-raleway text-[13px]">
                      <input
                        type="checkbox"
                        checked={statusFilter.includes(option.id)}
                        onChange={() => onToggleStatusFilter(option.id)}
                      />
                      <StatusDot status={option.id} />
                      {option.label}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            {tasks.map((task, index) => {
              const progress = findProgress(booking.applicant_checklist, task.step_id, task.id);
              const status = (progress?.status || "not_started") as ApplicantTaskStatus;
              return (
                <div key={`${task.step_id}-${task.id}`} className="rounded-xl border border-[#E9E9E9] bg-white p-5">
                  <div className="mb-2 flex items-start">
                    <span className="rounded-full bg-[#F3F4F6] px-3 py-1 font-raleway text-[12px]">Task {index + 1}</span>
                    <select
                      className="ml-auto rounded-full border border-[#E9E9E9] px-3 py-1 font-raleway text-[13px]"
                      value={status}
                      aria-label={`Status for ${task.title || "task"}`}
                      onChange={(event) => persistStatus(task.step_id, task.id, event.target.value as ApplicantTaskStatus)}
                    >
                      {TASK_STATUS_OPTIONS.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    <button className="ml-1 text-[#808080]" aria-label="Task actions" onClick={() => onOpenMenu(openTaskMenu === task.id ? null : task.id)}>
                      <MoreVert />
                    </button>
                  </div>
                  {openTaskMenu === task.id ? (
                    <div className="mb-2 ml-auto w-48 rounded-lg border border-[#E9E9E9] bg-white py-1 shadow-md">
                      <button className="block w-full px-3 py-2 text-left font-raleway text-[13px]" onClick={() => onOpenActivity(task.step_id, task.id)}>
                        Check Activity
                      </button>
                      <button className="block w-full px-3 py-2 text-left font-raleway text-[13px]" onClick={() => onMailAgency(task.agency?.email)}>
                        Mail Agency
                      </button>
                    </div>
                  ) : null}
                  <p className="font-raleway text-[12px] text-[#808080]">{task.step_title}</p>
                  <h3 className="font-raleway text-[18px] font-semibold">{task.title || "Untitled task"}</h3>
                  <p className="mt-1 font-open-sans text-[14px] text-[#4D4D4D]">{task.description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hasAgencyContact(task.agency) ? (
                      <span className="rounded-full bg-[#EFF8FA] px-3 py-1 font-raleway text-[12px] text-primary-200">{task.agency?.name}</span>
                    ) : null}
                    {task.agency?.email ? (
                      <span className="inline-flex items-center rounded-full bg-[#EFF8FA] px-3 py-1 font-raleway text-[12px] text-primary-200">
                        <MailOutline className="mr-1" style={{ height: 14, width: 14 }} />
                        {task.agency.email}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

type TaskActivityViewProps = {
  resource: ResourceSchema
  booking: ResourceBookingSchema
  stepId: string
  taskId: string
  comment: string
  attachment: File | null
  onCommentChange: (value: string) => void
  onAttachmentChange: (file: File | null) => void
  onBack: () => void
  onSubmit: () => void
};

const TaskActivityView = ({
  booking,
  stepId,
  taskId,
  comment,
  attachment,
  onCommentChange,
  onAttachmentChange,
  onBack,
  onSubmit,
}: TaskActivityViewProps) => {
  const progress = findProgress(booking.applicant_checklist, stepId, taskId);
  const activity = progress?.activity || [];

  return (
    <div className="mx-auto w-11/12 max-w-3xl py-6">
      <div className="mb-4 flex items-center">
        <button className="mr-3 flex h-10 w-10 items-center justify-center rounded-full border border-[#E9E9E9]" onClick={onBack} aria-label="Back">
          <ArrowBack />
        </button>
        <div>
          <h2 className="font-raleway text-[22px] font-bold">Task Activity</h2>
          <p className="font-open-sans text-[14px] text-[#808080]">View all messages, uploads, and activity for this task.</p>
        </div>
      </div>
      <div className="mb-4 rounded-xl border border-[#E9E9E9] bg-white p-4">
        <textarea
          className="min-h-[80px] w-full resize-none font-open-sans text-[14px] outline-none"
          placeholder="Add a comment..."
          value={comment}
          onChange={(event) => onCommentChange(event.target.value)}
        />
        <div className="mt-2 flex items-center">
          <label className="cursor-pointer text-[#808080]">
            <AttachFile />
            <input
              type="file"
              className="hidden"
              onChange={(event) => onAttachmentChange(event.target.files?.[0] || null)}
            />
          </label>
          {attachment ? <span className="ml-2 font-open-sans text-[12px] text-[#4D4D4D]">{attachment.name}</span> : null}
          <div className="ml-auto flex gap-2">
            <button className="rounded-md border border-[#E9E9E9] px-3 py-1.5 font-raleway text-[14px]" onClick={() => { onCommentChange(""); onAttachmentChange(null); }}>
              Cancel
            </button>
            <button
              className="rounded-md bg-primary-200 px-3 py-1.5 font-raleway text-[14px] text-white disabled:bg-[#E9E9E9] disabled:text-[#808080]"
              disabled={!comment.trim() && !attachment}
              onClick={onSubmit}
            >
              Comment
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {activity.length === 0 ? (
          <p className="py-8 text-center font-open-sans text-[#808080]">No activity yet for this task.</p>
        ) : (
          activity.map((item) => (
            <div key={item.id} className="rounded-xl bg-[#F8FAFB] p-4">
              <div className="mb-2 flex items-center gap-2">
                <UserAvatar firstName={item.author_name} avatar={item.author_avatar} size="sm" />
                <p className="font-raleway text-[14px] font-semibold">{item.author_name || "Admin"}</p>
                <p className="font-open-sans text-[12px] text-[#808080]">{item.created ? formatRelativeSubmitted(item.created) : ""}</p>
              </div>
              {item.message ? <p className="font-open-sans text-[14px] text-[#333333]">{item.message}</p> : null}
              {(item.attachments || []).map((file) => (
                <div key={file.file_url} className="mt-2 flex items-center rounded-full bg-white px-3 py-2">
                  <AttachFile className="mr-2 text-primary-400" style={{ height: 16, width: 16 }} />
                  <span className="font-open-sans text-[13px]">{file.file_name}</span>
                  {file.file_url ? (
                    <a className="ml-auto font-raleway text-[13px] text-primary-400" href={file.file_url} target="_blank" rel="noreferrer">
                      View
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentationApplicantsPanel;
